import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  ErrorBar,
  Line,
  LineChart,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartCard, EmptyState } from "./ChartCard";
import { chartTheme } from "./chartTheme";
import { useMetricsTimeToClose } from "./useMetricsQueries";
import { useMetricsFilter } from "./MetricsFilterContext";
import { buildPipelineDrillThroughUrl } from "@/lib/pipelineFilterEncoder";
import type {
  CycleCohortPoint,
  CycleTimeBucket,
  CycleTimeBySizeBox,
} from "@/api/dealFlow/metrics";

// Time to Close tab (3 charts).

export function TimeToCloseMetricsSection() {
  const { filter } = useMetricsFilter();
  const query = useMetricsTimeToClose(filter);
  if (query.isLoading) return <Loading />;
  if (query.error || !query.data) return <Error />;
  const { cohortLines, cycleTimeDistribution, cycleTimeBySizeBand } = query.data;

  return (
    <div
      className="grid grid-cols-1 gap-space-4 lg:grid-cols-2"
      data-testid="metrics-loaded"
    >
      <ChartCard
        title="Closed-deal cohort cycle time"
        hint="Average days to close, grouped by submission quarter."
        testId="chart-cohorts"
      >
        <CohortChart data={cohortLines} />
      </ChartCard>
      <ChartCard
        title="Cycle time distribution"
        hint="Click a band to drill through to deals in that cycle band."
        testId="chart-cycle-dist"
      >
        <CycleDistributionChart data={cycleTimeDistribution} />
      </ChartCard>
      <ChartCard
        title="Cycle time by deal size"
        hint="Box plot: min · Q1 · median · Q3 · max."
        testId="chart-cycle-size"
      >
        <CycleBySizeBox data={cycleTimeBySizeBand} />
      </ChartCard>
    </div>
  );
}

function CohortChart({ data }: { data: CycleCohortPoint[] }) {
  if (data.length === 0) return <EmptyState label="No closed deals yet." />;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ left: 16, right: 16 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis dataKey="cohortQuarter" stroke={chartTheme.axis} fontSize={11} />
        <YAxis stroke={chartTheme.axis} />
        <Tooltip />
        <Line
          type="monotone"
          dataKey="averageDays"
          stroke={chartTheme.primary}
          strokeWidth={2}
          dot={{ r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function CycleDistributionChart({ data }: { data: CycleTimeBucket[] }) {
  const navigate = useNavigate();
  if (data.every((d) => d.dealCount === 0)) return <EmptyState label="No closed deals." />;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ left: 16, right: 16 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis dataKey="band" stroke={chartTheme.axis} fontSize={11} />
        <YAxis stroke={chartTheme.axis} />
        <Tooltip />
        <Bar
          dataKey="dealCount"
          cursor="pointer"
          onClick={(entry) => {
            const band = (entry as unknown as CycleTimeBucket).band;
            navigate(buildPipelineDrillThroughUrl({ cycleTimeBand: band }));
          }}
        >
          {data.map((d) => (
            <Cell key={d.band} fill={chartTheme.primary} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function CycleBySizeBox({ data }: { data: CycleTimeBySizeBox[] }) {
  // Same box-plot composition as the analyst-load chart — see that file for
  // the design rationale (Recharts has no native <Box>; ComposedChart +
  // Scatter + ErrorBar gives a faithful, dependency-light surrogate).
  if (data.length === 0) return <EmptyState label="No data." />;
  const rows = data.map((d) => ({
    band: d.sizeBand,
    median: d.medianDays,
    iqr: d.q3Days - d.q1Days,
    iqrStart: d.q1Days,
    whiskerLow: d.medianDays - d.minDays,
    whiskerHigh: d.maxDays - d.medianDays,
  }));
  return (
    <ResponsiveContainer width="100%" height={320}>
      <ComposedChart data={rows} layout="vertical" margin={{ left: 16, right: 16 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis type="number" stroke={chartTheme.axis} />
        <YAxis dataKey="band" type="category" width={120} stroke={chartTheme.axis} />
        <Tooltip />
        <Bar dataKey="iqr" fill={chartTheme.primary} opacity={0.6} />
        <Scatter dataKey="median" fill={chartTheme.onyx}>
          <ErrorBar dataKey="whiskerLow" direction="x" width={6} stroke={chartTheme.axis} />
          <ErrorBar dataKey="whiskerHigh" direction="x" width={6} stroke={chartTheme.axis} />
        </Scatter>
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function Loading() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-gray-mid">
      Loading time-to-close…
    </div>
  );
}
function Error() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-status-error">
      Failed to load time-to-close metrics.
    </div>
  );
}
